import React, { useState, useEffect, useMemo, useCallback } from 'react';

import PropTypes from 'prop-types';
import { ColumnShape } from 'react-base-table';

import * as analytics from '@adsk/acs-web-analytics';
import { useCurrentProjectId, useCurrentToolId } from '@adsk/alloy-provider';

import { EntitiesTable } from '@adsk/acc-reference-picker';
import { SMALL_ORDERS, ORDERS } from './helpers/constants';
import useTableColumns from './helpers/useTableColumns';
import { getSdk } from '../helpers/getSdk';
import { AssigneeType, CustomAttribute, Rfi, RFIFilter, RfiStatus } from '../../../sdk';
import { getRfisListTrackEvent, translate } from '../../../helpers';
import { MaintenanceMode } from '../../../components';

/**
 * A component for showing custom data view.
 * Used to show the search result, in any way the plugin wish to.
 * Take a look at `EntityPickerModalListContent.js` for reference.
 *
 * @param {object} plugin - the current plugin
 * @param {Array<object>} entities - list of entities as returned from `queryEntities`
 * @param {func} onLoadMore - handler to fetch next page, as retrieved from `queryEntities`.
 * @param {Array<string>} disabledIds - array of IDs we want to mark as disabled.
 * @param {Array<string>} selectedIds - array of selected entity IDs.
 * @param {boolean} multiple - whether the component is multi selectable.
 * @param {func} onChange - handler for changing selection state. Shape: ({entity, checked}) => *
 * @param {func} setSelection - handler for add selection entities. Shape: (selectedNodes) => *
 * @param {boolean} loading - whether we're loading something from BE.
 * @param {boolean} error - whether we had errors loading something from BE.
 * @param {func} onTryAgain - callback to fetch data again.
 * @param {object} filters - the currently applied filters.
 * @param {func} addEntitiesToCache - A handler to add entities to cache in case the view fetch more entities. Shape: () => {entities, domain, type}
 * @param {object} dataViewProps - Additional props passed to CustomDataView.
 * @param {string} projectId - Current project ID
 * @param {func} onUpdate - callback to update an entity
 * @param {object} sort - Current sort options
 * @param {func} setSort - Callback for setting sort options.
 */

type EntitiesTableProps = React.ComponentProps<typeof EntitiesTable>;
type SharedWithEntitiesTable = Pick<
  EntitiesTableProps,
  | 'onLoadMore'
  | 'disabledIds'
  | 'selectedIds'
  | 'multiple'
  | 'setSelection'
  | 'loading'
  | 'error'
  | 'onTryAgain'
>;
type RFICustomDataViewProps = SharedWithEntitiesTable & {
  entities: Rfi[];
  onChange: ({ entity, checked }: { entity: { id: string }; checked?: boolean }) => void;
  setSort: ({
    field,
    order,
    columnName,
  }: {
    field: string;
    columnName: string;
    order: string;
  }) => void;
  filters: RFIFilter;
  sort: { columnName: string; order: string };
  dataViewProps: {
    sourceEntityType: string;
    sourceEntityId: string;
    sourceEntityDomain: string;
  };
};

type renderSelectionCellArgs = Pick<
  RFICustomDataViewProps,
  'multiple' | 'onChange' | 'disabledIds' | 'selectedIds'
> & { rowData: { id: string } };
const renderSelectionCell = ({
  rowData,
  multiple,
  onChange,
  disabledIds,
  selectedIds,
}: renderSelectionCellArgs) => (
  <EntitiesTable.SelectionCell
    multiple={multiple}
    rowData={rowData}
    onChange={onChange}
    disabledIds={disabledIds}
    selectedIds={selectedIds}
  />
);

type renderSelectionHeaderArgs = Pick<
  RFICustomDataViewProps,
  'setSelection' | 'disabledIds' | 'selectedIds'
> & { filteredEntities: RFICustomDataViewProps['entities'] };

const renderSelectionHeader = ({
  filteredEntities,
  selectedIds,
  disabledIds,
  setSelection,
}: renderSelectionHeaderArgs) => (
  <EntitiesTable.SelectionCellHeader
    entities={filteredEntities}
    selectedIds={selectedIds}
    disabledIds={disabledIds}
    setSelection={setSelection}
  />
);

const RFICustomDataView = ({
  entities = [],
  onLoadMore,
  disabledIds = [],
  selectedIds = [],
  multiple,
  onChange,
  setSelection,
  loading,
  error,
  onTryAgain,
  filters = {},
  setSort,
  sort,
  dataViewProps,
}: RFICustomDataViewProps) => {
  const projectId = useCurrentProjectId();
  const [customAttributes, setCustomAttributes] = useState<CustomAttribute[]>([]);
  const currentToolId = useCurrentToolId();

  const getActiveCustomAttributes = (customAttributes: CustomAttribute[]) =>
    customAttributes.filter(({ isActive }) => isActive);

  const dataColumns = useTableColumns(getActiveCustomAttributes(customAttributes));

  const filteredEntities = useMemo(
    () =>
      dataViewProps?.sourceEntityType === 'rfi' &&
      dataViewProps.sourceEntityDomain === 'autodesk-bim360-rfi'
        ? entities.filter((item) => item.id !== dataViewProps.sourceEntityId)
        : entities,
    [entities, dataViewProps],
  );

  const columns = useMemo(
    () =>
      [
        {
          key: 'selection',
          width: '40px',
          minWidth: '40px',
          cellRenderer: ({ rowData }) =>
            renderSelectionCell({ rowData, multiple, onChange, disabledIds, selectedIds }),
          ...(multiple && {
            headerRenderer: renderSelectionHeader({
              filteredEntities,
              selectedIds,
              disabledIds,
              setSelection,
            }),
          }),
          selectedIds,
          entities: filteredEntities,
        },
        ...dataColumns,
      ] as ColumnShape<Rfi>[],
    [filteredEntities, multiple, disabledIds, selectedIds, onChange, setSelection, dataColumns],
  );
  useEffect(() => {
    const { eventName, properties } = getRfisListTrackEvent(currentToolId, 'unknown');
    analytics.track(eventName, properties);
  }, [currentToolId]);

  useEffect(() => {
    if (!projectId) {
      return;
    }
    const sdk = getSdk(projectId);
    async function fetchCustomAttributes() {
      const { results } = await sdk.CustomAttributes.getAll({
        filterStatus: ['active', 'inactive'],
        limit: 200,
      });
      setCustomAttributes(results);
    }
    fetchCustomAttributes();
  }, [projectId]);

  const handleColumnSort = useCallback(
    ({ column, order }) => {
      setSort?.({
        field: column.sortField,
        order: order === SMALL_ORDERS.ASCENDING ? ORDERS.ASCENDING : ORDERS.DESCENDING,
        columnName: column.key,
      });
    },
    [setSort],
  );

  const sortState = useMemo(
    () =>
      sort && {
        [sort.columnName]:
          sort.order === ORDERS.ASCENDING ? SMALL_ORDERS.ASCENDING : SMALL_ORDERS.DESCENDING,
      },
    [sort],
  );
  return (
    <MaintenanceMode>
      <EntitiesTable
        loadingMoreText={translate('rpPlugin.FETCHING_MORE_RESULTS')}
        errorText={translate('rpPlugin.COULD_NOT_LOAD_CONTENT')}
        columns={columns}
        disabledIds={disabledIds}
        selectedIds={selectedIds}
        entities={filteredEntities}
        filters={filters}
        onLoadMore={onLoadMore}
        onChange={onChange}
        multiple={multiple}
        loading={loading}
        error={error}
        setSelection={setSelection}
        onTryAgain={onTryAgain}
        onColumnSort={handleColumnSort}
        sortState={sortState}
      />
    </MaintenanceMode>
  );
};

RFICustomDataView.propTypes = {
  /**
   * Whether we're loading something from BE.
   */
  loading: PropTypes.bool,
  /**
   * Whether had error fetching data from BE.
   */
  error: PropTypes.bool,
  /**
   * Callback to fetch data again.
   */
  onTryAgain: PropTypes.func,
  /**
   * Load the next page of entities.
   */
  onLoadMore: PropTypes.func,
  /**
   * IDs of selected entities.
   */
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * IDs of entities we want to mark as disabled.
   */
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * Whether the component is multi selectable.
   */
  multiple: PropTypes.bool,
  /**
   * List of RFI (look in rfis/sdk/types/RFI.ts).
   */
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      customIdentifier: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      status: PropTypes.oneOf(Object.values(RfiStatus)).isRequired,
      assignedTo: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          type: PropTypes.oneOf(Object.values(AssigneeType)),
        }),
      ),
      dueDate: PropTypes.string,
      locationDescription: PropTypes.string,
      priority: PropTypes.oneOf(['Low', 'Normal', 'High']),
      createdBy: PropTypes.string.isRequired,
      locations: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  /**
   * Callback to set Selections.
   * Handler is of shape: (selectedNodes) => *
   */
  setSelection: PropTypes.func,
  /**
   * Callback to change selection.
   */
  onChange: PropTypes.func,
  /**
   * The currently applied filters.
   */
  filters: PropTypes.object,
  /**
   * Callback for setting sort options.
   */
  setSort: PropTypes.func,
  /**
   * Current sort options
   */
  sort: PropTypes.object,
  /**
   * Custom props for data views
   */
  dataViewProps: PropTypes.shape({
    dataFilter: PropTypes.func,
    sourceEntityId: PropTypes.string,
    sourceEntityDomain: PropTypes.string,
    sourceEntityType: PropTypes.string,
  }),
};

export default RFICustomDataView;
